import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { AssessmentState } from './state';
import Assessments from '@/services/Assessments';
import { Assessment, DexExamGroup } from '@/models/greathall/assessment.model';
import { UploadFileInput, UploadReplyInput } from '@/store/mutations/westerosGreathallMutation';
import SentryClient from '@/helpers/sentry';

const actions: ActionTree<AssessmentState, RootState> = {
  async PULL_MINIMUM_ASSESSMENT_DATA({ commit }, payload: { dexCourseId: number }) {
    commit('SET_ASSESSMENTS', []);

    const { dexCourseId } = payload;

    try {
      const response = await Assessments.minimunAssessmentsData(dexCourseId);
      const minAssessmentsData = response.sort(
        ({ minimumContractDays: a }: Assessment, { minimumContractDays: b }: Assessment) => a - b,
      );

      commit('SET_ASSESSMENTS', minAssessmentsData);
    } catch (error) {
      SentryClient.captureError(error as Error);
    }
  },

  async PULL_ASSESSMENT_RESULTS_DATA(
    { commit },
    payload: { dexCourseId: number; assessmentId: number },
  ) {
    commit('SET_ASSESSMENT_DETAILS', {});

    const { dexCourseId, assessmentId } = payload;

    const assessmentDetails = await Assessments.assessmentResultsData(assessmentId, dexCourseId);

    commit('SET_ASSESSMENT_DETAILS', assessmentDetails);
  },

  async PULL_ASSESSMENT_MODULE_ONE_DATA(
    { commit },
    payload: { dexCourseId: number; assessmentId: number },
  ) {
    commit('SET_ASSESSMENT_DETAILS', {});

    const { dexCourseId, assessmentId } = payload;

    const response = await Assessments.assessmentModuleOneData(assessmentId, dexCourseId);

    const dexExamGroup = response.dex.reduce(
      (acc: DexExamGroup, curr: any) => {
        acc.totalDuration += curr.examByExamId.duration;
        acc.totalQuestions += curr.examByExamId.examQuestionsByExamId.totalCount;
        return acc;
      },
      { totalDuration: 0, totalQuestions: 0 },
    );

    const assessmentDetails = {
      ...response.westeros,
      dexExamGroup,
    };

    commit('SET_ASSESSMENT_DETAILS', assessmentDetails);
  },

  async PULL_ASSESSMENT_MODULE_ONE_VARIANT_LINK(
    { commit },
    payload: { dexCourseId: number; assessmentId: number },
  ) {
    commit('SET_ASSESSMENT_DETAILS', {});

    const { dexCourseId, assessmentId } = payload;

    const assessmentDetails = await Assessments.assessmentModuleOneVariantLink(
      assessmentId,
      dexCourseId,
    );

    commit('SET_ASSESSMENT_DETAILS', assessmentDetails);
  },

  async PULL_ASSESSMENT_MODULE_TWO_DATA(
    { commit },
    payload: { dexCourseId: number; assessmentId: number },
  ) {
    commit('SET_ASSESSMENT_DETAILS', {});

    const { dexCourseId, assessmentId } = payload;

    const assessmentDetails = await Assessments.assessmentModuleTwoData(assessmentId, dexCourseId);

    commit('SET_ASSESSMENT_DETAILS', assessmentDetails);
  },

  async PULL_ASSESSMENT_TCC_DATA(
    { commit },
    payload: { dexCourseId: number; assessmentId: number },
  ) {
    commit('SET_ASSESSMENT_DETAILS', {});

    const { dexCourseId, assessmentId } = payload;

    const assessmentDetails = await Assessments.assessmentTCCData(assessmentId, dexCourseId);

    commit('SET_ASSESSMENT_DETAILS', assessmentDetails);
  },

  async SEND_ASSESSMENT_REPLY_FILE(_, payload: { dexCourseId: number; data: UploadReplyInput }) {
    const { dexCourseId, data } = payload;

    await Assessments.sendAssessmentReplyFile(dexCourseId, data);
  },

  async RESET_DEX_ASSESSMENT(_, payload: { assessmentResultId: number; dexCourseId: number }) {
    const { assessmentResultId, dexCourseId } = payload;

    await Assessments.resetDexAssignment(assessmentResultId, dexCourseId);
  },

  async SEND_PARTIAL_TCC(
    _,
    payload: {
      assessmentResultId: number;
      dexCourseId: number;
      fileName: string;
      dataUrl: string;
    },
  ) {
    const { assessmentResultId, dexCourseId, fileName, dataUrl } = payload;

    await Assessments.sendPartialTCC(assessmentResultId, dexCourseId, fileName, dataUrl);
  },

  async SEND_FINAL_TCC(
    _,
    payload: {
      finalTCCFile: UploadFileInput;
      quizFile: UploadFileInput;
      assessmentResultId: number;
      dexCourseId: number;
    },
  ) {
    const { finalTCCFile, quizFile, assessmentResultId, dexCourseId } = payload;

    await Assessments.sendFinalTCC(finalTCCFile, quizFile, assessmentResultId, dexCourseId);
  },

  async SEND_TCC_THEME(
    _,
    payload: { tccTheme: string; assessmentId: number; dexCourseId: number },
  ) {
    const { tccTheme, assessmentId, dexCourseId } = payload;

    await Assessments.sendTCCTheme(tccTheme, assessmentId, dexCourseId);
  },

  async REQUEST_TCC_REVALUATION(
    _,
    payload: {
      pleadingText: string;
      assessmentResultId: number;
      dexCourseId: number;
    },
  ) {
    const { pleadingText, assessmentResultId, dexCourseId } = payload;

    await Assessments.requestTCCRevaluation(pleadingText, assessmentResultId, dexCourseId);
  },

  async START_TCC_RETRY(
    _,
    payload: {
      assessmentId: number;
      dexCourseId: number;
      useSameTheme: boolean;
    },
  ) {
    const { assessmentId, dexCourseId, useSameTheme } = payload;

    await Assessments.startTCCRetry(assessmentId, dexCourseId, useSameTheme);
  },
};

export default actions;
